/**
 * SoundConfigFlyover — Mode-specific advanced settings for the SoundExpert node.
 *
 * Dismissal and portal rendering are handled by the parent via FlyoutPortal.
 *
 * Content varies by audio mode:
 *   - SFX: Duration (Auto/1s/5s/Custom) + Loop (Off/On)
 *   - Music: Duration (Auto/30s/60s/Custom) + Lyrics (Auto/Custom/Instrumental)
 *   - Speech: Voice profile selector (searchable list), also reused inline
 */

import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type CharacterVoiceCatalogueEntry } from '../../../../api/client'
import { type VoiceProfileDict, type VoiceProfileDictYMap } from '../../../../generated/nodeDataTypes'
import { createNadDebug } from '../../../../utils/debugLog'
import { useVoiceCatalogueModal } from './VoiceCatalogueModalProvider'

// --- Types ------------------------------------------------------------------

export interface SoundConfigFlyoverProps {
  mode: string
  duration: string
  loop: string
  lyricsMode: string
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  selectedVoiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  targetVoiceBrief?: VoiceProfileDict | VoiceProfileDictYMap
  onDurationChange: (value: string) => void
  onLoopChange: (value: string) => void
  onLyricsModeChange: (value: string) => void
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void | Promise<void>
}

export interface SoundExpertSpeechVoicePanelProps {
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  selectedVoiceProfile?: VoiceProfileDict | VoiceProfileDictYMap
  targetVoiceBrief?: VoiceProfileDict | VoiceProfileDictYMap
  onVoiceProfileChange?: (profile: VoiceProfileDict | undefined) => void | Promise<void>
  className?: string
}

// --- Constants --------------------------------------------------------------

const SFX_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '1s', value: '1s' },
  { label: '5s', value: '5s' },
  { label: '10s', value: '10s' },
]

const MUSIC_DURATION_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: '30s', value: '30s' },
  { label: '60s', value: '60s' },
  { label: '120s', value: '120s' },
]

const LOOP_OPTIONS = [
  { label: 'Off', value: 'off' },
  { label: 'On', value: 'on' },
]

const LYRICS_OPTIONS = [
  { label: 'Auto', value: 'auto' },
  { label: 'Custom', value: 'custom' },
  { label: 'Instrumental', value: 'instrumental' },
]

/** Map option value → i18n key for translatable labels; missing entries use the raw label. */
const OPTION_LABEL_KEYS: Record<string, string> = {
  auto: 'soundConfigFlyover.auto',
  off: 'soundConfigFlyover.off',
  on: 'soundConfigFlyover.on',
  custom: 'soundConfigFlyover.custom',
  instrumental: 'soundConfigFlyover.instrumental',
}

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'
const voiceProfileLog = createNadDebug('dw:sound-expert-voice-profile')
const WAVEFORM_BARS = [10, 18, 14, 24, 16, 28, 20, 12, 22, 15, 26, 18]

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary/20 text-primary border border-primary/40'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

function toPlainVoiceProfile(
  voiceProfile?: VoiceProfileDict | VoiceProfileDictYMap,
): VoiceProfileDict | undefined {
  if (!voiceProfile) return undefined
  return {
    provider: voiceProfile.provider,
    voice_id: voiceProfile.voice_id,
    voice_name: voiceProfile.voice_name,
    description: voiceProfile.description,
    voice_prompt: voiceProfile.voice_prompt,
    name: voiceProfile.name,
    audio_sample: voiceProfile.audio_sample,
    speech_text: voiceProfile.speech_text,
    characteristics: voiceProfile.characteristics ? [...voiceProfile.characteristics] : undefined,
    pronunciation_guide: voiceProfile.pronunciation_guide,
    voice_source: voiceProfile.voice_source,
    voice_ref: voiceProfile.voice_ref,
    language: voiceProfile.language,
    locale: voiceProfile.locale,
    use_case: voiceProfile.use_case,
    descriptors: voiceProfile.descriptors ? [...voiceProfile.descriptors] : undefined,
  }
}

// --- Speech Voice Panel -----------------------------------------------------

export const SoundExpertSpeechVoicePanel = memo(function SoundExpertSpeechVoicePanel({
  voiceProfile,
  selectedVoiceProfile,
  targetVoiceBrief,
  onVoiceProfileChange,
  className,
}: SoundExpertSpeechVoicePanelProps) {
  const { t } = useTranslation('canvas')
  const [selectionError, setSelectionError] = useState<string | null>(null)
  const { openVoiceCatalogueModal } = useVoiceCatalogueModal()
  const selectedSourceProfile = selectedVoiceProfile ?? (targetVoiceBrief ? undefined : voiceProfile)
  const selectedProfile = useMemo(
    () => toPlainVoiceProfile(selectedSourceProfile),
    [selectedSourceProfile],
  )
  const targetBriefProfile = useMemo(
    () => toPlainVoiceProfile(targetVoiceBrief),
    [targetVoiceBrief],
  )
  const handleCatalogueVoiceSelect = useCallback(
    async (voice: CharacterVoiceCatalogueEntry): Promise<void> => {
      if (!onVoiceProfileChange) {
        throw new Error(t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.'))
      }
      setSelectionError(null)
      voiceProfileLog({
        event: 'catalogue-select:start',
        voice_id: voice.voice_id,
        voice_ref: voice.voice_ref,
      })
      try {
        // The backend resolves this opaque reference and owns the concrete
        // profile. Do not publish catalogue metadata from the browser.
        await onVoiceProfileChange({ voice_ref: voice.voice_ref })
        voiceProfileLog({
          event: 'catalogue-select:success',
          voice_id: voice.voice_id,
          voice_source: voice.voice_source,
        })
      } catch (error) {
        const message = error instanceof Error ? error.message : t('soundConfigFlyover.voiceSelectionFailed')
        setSelectionError(message)
        voiceProfileLog({
          event: 'catalogue-select:error',
          voice_id: voice.voice_id,
          error: message,
        })
        throw new Error(message)
      }
    },
    [onVoiceProfileChange, t],
  )

  const openCatalogue = useCallback(() => {
    openVoiceCatalogueModal({
      kind: 'sound_expert',
      targetLabel:
        selectedProfile?.voice_name ||
        selectedProfile?.voice_id ||
        targetBriefProfile?.name ||
        t('soundConfigFlyover.voiceProfile'),
      selectedVoiceRef: selectedProfile?.voice_ref,
      onApply: handleCatalogueVoiceSelect,
    })
  }, [
    handleCatalogueVoiceSelect,
    openVoiceCatalogueModal,
    selectedProfile,
    targetBriefProfile,
    t,
  ])

  const clearVoice = useCallback(async () => {
    if (!onVoiceProfileChange) return
    setSelectionError(null)
    try {
      await onVoiceProfileChange(undefined)
    } catch (error) {
      setSelectionError(
        error instanceof Error ? error.message : t('soundConfigFlyover.voiceSelectionFailed'),
      )
    }
  }, [onVoiceProfileChange, t])

  const unavailableReason = onVoiceProfileChange
    ? null
    : t('soundConfigFlyover.voiceSelectionUnavailable', 'Voice selection is unavailable.')

  return (
    <div
      className={`flex flex-col gap-3 ${className ?? 'w-[26rem] max-w-[calc(100vw-2rem)]'}`}
      data-testid="speech-config"
    >
      <label className={LABEL_CLASS}>{t('soundConfigFlyover.voiceProfile')}</label>

      {selectedProfile ? (
        <div
          className="rounded-lg border border-input-border bg-surface-1 p-2"
          data-testid="current-sound-voice-profile"
        >
          <div className="flex items-center gap-2">
            <div
              className="flex h-8 w-12 shrink-0 items-end gap-0.5 rounded-lg border border-input-border bg-input-bg px-1.5 py-1"
              aria-hidden="true"
            >
              {WAVEFORM_BARS.map((height, index) => (
                <span
                  key={`${height}-${index}`}
                  className="w-0.5 rounded-full bg-primary/70"
                  style={{ height }}
                />
              ))}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xxs font-medium uppercase text-text-muted">
                {t('soundConfigFlyover.selectedVoice', 'Selected voice')}
              </p>
              <p className="truncate text-xs font-semibold text-foreground">
                {selectedProfile?.voice_name
                  || selectedProfile?.voice_id
                  || selectedProfile?.name
                  || t('voiceTabContent.unnamedVoice')}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {selectedProfile?.provider ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {selectedProfile.provider}
                  </span>
                ) : null}
                {selectedProfile?.language ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {selectedProfile.language}
                  </span>
                ) : null}
                {selectedProfile?.use_case ? (
                  <span className="rounded-full border border-input-border bg-input-bg px-1.5 py-0.5 text-xxs text-text-muted">
                    {selectedProfile.use_case}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={openCatalogue}
                disabled={Boolean(unavailableReason)}
                title={unavailableReason ?? undefined}
                className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="sound-expert-voice-catalogue-trigger"
              >
                {selectedProfile
                  ? t('soundConfigFlyover.changeVoice')
                  : t('voiceTabContent.selectVoice', 'Select Voice')}
              </button>
              {selectedProfile ? (
                <button
                  onClick={() => void clearVoice()}
                  className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3"
                  data-testid="clear-voice-btn"
                >
                  {t('soundConfigFlyover.clear')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-surface-1 px-2 py-2">
          <span className="min-w-0 flex-1 truncate text-xs font-medium text-text-muted">
            {t('soundConfigFlyover.noVoiceSelected')}
          </span>
          <button
            type="button"
            onClick={openCatalogue}
            disabled={Boolean(unavailableReason)}
            title={unavailableReason ?? undefined}
            className="h-7 rounded-lg border border-input-border bg-surface-0 px-2 text-xs font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="sound-expert-voice-catalogue-trigger"
          >
            {t('voiceTabContent.selectVoice', 'Select Voice')}
          </button>
        </div>
      )}
      {selectionError ? (
        <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs font-medium text-foreground">
          {selectionError}
        </p>
      ) : null}
    </div>
  )
})

// --- Component --------------------------------------------------------------

export const SoundConfigFlyover = memo(function SoundConfigFlyover({
  mode,
  duration,
  loop,
  lyricsMode,
  voiceProfile,
  selectedVoiceProfile,
  targetVoiceBrief,
  onDurationChange,
  onLoopChange,
  onLyricsModeChange,
  onVoiceProfileChange,
}: SoundConfigFlyoverProps) {
  const { t } = useTranslation('canvas')

  const handleDurationClick = useCallback(
    (value: string) => onDurationChange(value),
    [onDurationChange],
  )

  // -- Render SFX config -----------------------------------------------------
  const renderSfxConfig = () => (
    <div className="flex flex-col gap-3" data-testid="sfx-config">
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.duration')}</label>
        <div className="flex gap-1">
          {SFX_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              data-testid={`sfx-duration-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.loop')}</label>
        <div className="flex gap-1">
          {LOOP_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLoopChange(opt.value)}
              className={segmentedBtnClass(loop === opt.value)}
              data-testid={`loop-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // -- Render Music config ---------------------------------------------------
  const renderMusicConfig = () => (
    <div className="flex flex-col gap-3" data-testid="music-config">
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.musicDuration')}</label>
        <div className="flex gap-1">
          {MUSIC_DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleDurationClick(opt.value)}
              className={segmentedBtnClass(duration === opt.value)}
              data-testid={`music-duration-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className={LABEL_CLASS}>{t('soundConfigFlyover.lyrics')}</label>
        <div className="flex gap-1">
          {LYRICS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onLyricsModeChange(opt.value)}
              className={segmentedBtnClass(lyricsMode === opt.value)}
              data-testid={`lyrics-${opt.value}`}
            >
              {OPTION_LABEL_KEYS[opt.value] ? t(OPTION_LABEL_KEYS[opt.value]) : opt.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  const renderSpeechConfig = () => (
    <SoundExpertSpeechVoicePanel
      voiceProfile={voiceProfile}
      selectedVoiceProfile={selectedVoiceProfile}
      targetVoiceBrief={targetVoiceBrief}
      onVoiceProfileChange={onVoiceProfileChange}
    />
  )

  return (
    <div data-testid="sound-config-flyover">
      {mode === 'sfx' && renderSfxConfig()}
      {mode === 'music' && renderMusicConfig()}
      {mode === 'speech' && renderSpeechConfig()}
    </div>
  )
})

SoundConfigFlyover.displayName = 'SoundConfigFlyover'
